import type { Tier } from './evolution'
import type { ItemInstance, PlayerForm } from './components'

// Post-unification persisted state: the evolution tier and one-shot story
// flags. Old records also carried skills/powers purchase fields — those
// systems are gone (amoeba-unification.md); legacy fields are ignored.

const DB_NAME = 'amoeba'
const DB_VERSION = 1
const STORE = 'progress'
const KEY = 'singleton'

export interface PersistedProgress {
  schemaVersion: 1
  // `form` is the divergent-evolution critter, set once past the speciation
  // tier; a returning tier-3+ player keeps what it became. Absent pre-axe.
  evolution: { tier: Tier; form?: PlayerForm }
  flags?: { motherGift?: boolean }
  // Gear (A2, monorail-yqv): the durable collection + worn instance ids.
  // Additive optional field — v1 records without it load fine.
  gear?: {
    inventory: ItemInstance[]
    equipped: { hat?: string; weapon?: string }
  }
  // World layout (B2, monorail-x8w): the generated web is persistent per
  // save — seed regenerates the identical layout; doors carries opened
  // shortcut/lock state (written by B3/B4, schema reserved now). Additive
  // optional field; records without it roll a fresh seed on load.
  world?: {
    seed: number
    doors: Record<string, boolean>
  }
  // Key items (B4): traversal keys, permanent like gear. Additive field.
  keys?: string[]
  // Daughter-cell checkpoint attunement (monorail-s8k): the last-touched
  // cell's id. Additive optional field; absent = respawn at the origin hub.
  checkpoint?: string
}

let warnedUnavailable = false
let warnedFailure = false
function warnUnavailableOnce(): void {
  if (warnedUnavailable) return
  warnedUnavailable = true
  console.warn('[amoeba/persistence] indexedDB unavailable; running in-memory only')
}
function warnFailureOnce(label: string, err: unknown): void {
  if (warnedFailure) return
  warnedFailure = true
  console.warn(`[amoeba/persistence] ${label}`, err)
}

function getIDB(): IDBFactory | null {
  if (typeof globalThis === 'undefined') return null
  const idb = (globalThis as { indexedDB?: IDBFactory }).indexedDB
  return idb ?? null
}

function openDb(idb: IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = idb.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE)
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
    req.onblocked = () => reject(new Error('indexedDB open blocked'))
  })
}

export async function loadProgress(): Promise<PersistedProgress | null> {
  const idb = getIDB()
  if (!idb) {
    warnUnavailableOnce()
    return null
  }
  let db: IDBDatabase | null = null
  try {
    db = await openDb(idb)
    return await new Promise<PersistedProgress | null>((resolve, reject) => {
      const tx = db!.transaction(STORE, 'readonly')
      const store = tx.objectStore(STORE)
      const req = store.get(KEY)
      req.onsuccess = () => {
        const value = req.result as PersistedProgress | undefined
        if (!value) return resolve(null)
        if (value.schemaVersion !== 1) return resolve(null)
        resolve(value)
      }
      req.onerror = () => reject(req.error)
    })
  } catch (err) {
    warnFailureOnce('load failed', err)
    return null
  } finally {
    db?.close()
  }
}

export async function saveProgress(p: PersistedProgress): Promise<void> {
  const idb = getIDB()
  if (!idb) return
  let db: IDBDatabase | null = null
  try {
    db = await openDb(idb)
    await new Promise<void>((resolve, reject) => {
      const tx = db!.transaction(STORE, 'readwrite')
      const store = tx.objectStore(STORE)
      store.put(p, KEY)
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
      tx.onabort = () => reject(tx.error ?? new Error('indexedDB save aborted'))
    })
  } catch (err) {
    warnFailureOnce('save failed', err)
  } finally {
    db?.close()
  }
}

// Wipes all persisted progress. Used by the dev-console reset cheat and
// by any future player-facing reset affordance. Resolves silently when
// IDB is unavailable (matches saveProgress' fallback behaviour).
export async function clearProgress(): Promise<void> {
  const idb = getIDB()
  if (!idb) return
  let db: IDBDatabase | null = null
  try {
    db = await openDb(idb)
    await new Promise<void>((resolve, reject) => {
      const tx = db!.transaction(STORE, 'readwrite')
      tx.objectStore(STORE).delete(KEY)
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
      tx.onabort = () => reject(tx.error ?? new Error('indexedDB clear aborted'))
    })
  } catch (err) {
    warnFailureOnce('clear failed', err)
  } finally {
    db?.close()
  }
}

export function snapshotProgress(
  tier: Tier,
  flags?: { motherGift?: boolean },
  form?: PlayerForm,
  gear?: PersistedProgress['gear'],
  world?: PersistedProgress['world'],
  keys?: string[],
  checkpoint?: string,
): PersistedProgress {
  return {
    schemaVersion: 1,
    evolution: form ? { tier, form } : { tier },
    flags: { motherGift: flags?.motherGift ?? false },
    ...(gear && (gear.inventory.length > 0 || gear.equipped.hat || gear.equipped.weapon)
      ? { gear }
      : {}),
    ...(world ? { world } : {}),
    ...(keys && keys.length > 0 ? { keys } : {}),
    ...(checkpoint ? { checkpoint } : {}),
  }
}
